import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import DescriptionIcon from 'components/material-symbol/icons/Description'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import {
  PURCHASE_LIMIT_PERIOD_OPTIONS,
  TURNAROUND_HOURS_OPTIONS,
  VIRTUAL_COACHING_LEGAL_CAPTION,
} from '../offering-kind'
import ExpirationDurationDropdown from './expiration-duration-dropdown'
import { optionalLabel, requiredLabel } from './required-field'

const EXPIRATION_DESCRIPTION =
  'How long students have to use their swing analysis after buying. Choose Never if it should not expire.'

const VirtualCoachingSettings = ({ isCreateFlow, onApplyTemplate }) => {
  const editClasses = useEditFormStyles()
  const { control, errors } = useFormContext()

  return (
    <>
      {isCreateFlow ? (
        <Box mb={3}>
          <TipWell
            caption={
              <>
                <Box component="span" display="block" fontWeight={700}>
                  Start from a template
                </Box>
                Prefill the name, description, and turnaround time with a swing
                analysis template. You can edit everything afterwards.
              </>
            }
            action={
              <TipWellActionButton
                type="button"
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={onApplyTemplate}
                data-cy="virtualCoachingTemplate"
              >
                Use template
              </TipWellActionButton>
            }
          />
        </Box>
      ) : null}

      <Box className={editClasses.fieldGroup}>
        <Typography className={editClasses.sectionHeading}>
          Turnaround Time
        </Typography>
        <Typography className={editClasses.fieldHelper}>
          How long you commit to returning your analysis after the student
          uploads their video. Students see this before they buy.
        </Typography>
        <FormControl
          error={!!errors.turnaroundHours}
          variant="outlined"
          fullWidth
          className={editClasses.fieldInput}
        >
          <InputLabel id="turnaroundHours-label">
            {requiredLabel('Turnaround time')}
          </InputLabel>
          <Controller
            name="turnaroundHours"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelId="turnaroundHours-label"
                label={requiredLabel('Turnaround time')}
                data-cy="turnaroundHours"
                value={value ?? ''}
                onChange={onChange}
              >
                {TURNAROUND_HOURS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.turnaroundHours ? (
            <FormHelperText>Select a turnaround time</FormHelperText>
          ) : null}
        </FormControl>
      </Box>

      <Box className={editClasses.fieldGroup}>
        <Typography className={editClasses.sectionHeading}>
          {optionalLabel('Purchase Limit')}
        </Typography>
        <Typography className={editClasses.fieldHelper}>
          Limit how many of this offering can be purchased so your review queue
          stays manageable. Leave blank for no limit.
        </Typography>
        <Box display="flex" style={{ gap: 16 }}>
          <Controller
            name="purchaseLimit"
            control={control}
            render={({ onChange, onBlur, value }) => (
              <TextField
                id="purchaseLimit"
                label="Max purchases"
                data-cy="purchaseLimit"
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.purchaseLimit}
                helperText={errors.purchaseLimit?.message}
                variant="outlined"
                inputProps={{ inputMode: 'numeric' }}
                style={{ flex: 1 }}
              />
            )}
          />
          <FormControl variant="outlined" style={{ flex: 1 }}>
            <InputLabel id="purchaseLimitPeriod-label">Period</InputLabel>
            <Controller
              name="purchaseLimitPeriod"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  labelId="purchaseLimitPeriod-label"
                  label="Period"
                  data-cy="purchaseLimitPeriod"
                  value={value ?? 'WEEK'}
                  onChange={onChange}
                >
                  {PURCHASE_LIMIT_PERIOD_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Box>
      </Box>

      <ExpirationDurationDropdown
        isEditFlow
        description={EXPIRATION_DESCRIPTION}
      />

      <Box mb={3}>
        <TipWell
          variant="neutral"
          icon={InfoOutlinedIcon}
          caption={VIRTUAL_COACHING_LEGAL_CAPTION}
        />
      </Box>
    </>
  )
}

export default VirtualCoachingSettings
